import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { markBoot } from './utils/bootTrace';
import { requestedFault } from './features/support/devFaults';
import AuthProvider from './features/auth/AuthProvider';
import { AbilityProvider } from './features/auth/ability';
import { RxDBProvider } from './db/RxDBProvider';
import { UserDirectoryProvider } from './features/users/UserDirectoryProvider';
import { ConflictProvider } from './features/sync/ConflictProvider';
import { useIntl } from 'react-intl';
import { ToastProvider } from './components/ToastProvider';
import { useToast } from './components/toastContext';
import { onSaveInAppFailed } from './lib/nativeApp';
import { ConfirmProvider } from './components/ConfirmProvider';
import RootRedirect from './components/RootRedirect';
import { useRememberLocation } from './components/useRememberLocation';
import RequireOffice from './components/RequireOffice';
import LegacyOfficeRedirect from './components/LegacyOfficeRedirect';
import MediaRedirect from './features/media/MediaRedirect';
import NotFoundPage from './components/NotFoundPage';
import FullPageProgress from './components/FullPageProgress';
/**
 * Lazy: the whole office — every project page, the Auswertung's cross-project
 * grids, the Verwaltung, and the Excel/PDF machinery only they reach. It is by
 * far the larger half of the app, and a tablet that only ever opens
 * Feldaufnahme has no reason to download it. The office pays one fetch on its
 * first navigation, served from the same origin (and precached by the service
 * worker), while the drawer is already on screen.
 */
const OfficeApp = lazy(() => import('./OfficeApp'));
import FieldLayout from './features/field/FieldLayout';
import FieldProjectsPage from './features/field/FieldProjectsPage';
import FieldFindingsPage from './features/field/FieldFindingsPage';
import {
    FieldFindingDialogRoute,
    FieldPlanumDialogRoute,
    FieldProfileDialogRoute,
    FieldDiaryDialogRoute,
    FieldOverallDescriptionDialogRoute,
    FieldAreaDescriptionDialogRoute,
    FieldSampleDialogRoute
} from './features/field/fieldRecordDialogRoutes';
import FieldTimeEntriesPage, {
    FieldEquipmentTimeEntriesPage
} from './features/field/FieldTimeEntriesPage';
import FieldDiaryPage from './features/field/FieldDiaryPage';
import FieldOverallDescriptionsPage from './features/field/FieldOverallDescriptionsPage';
import FieldAreaDescriptionsPage from './features/field/FieldAreaDescriptionsPage';
import FieldSamplesPage from './features/field/FieldSamplesPage';

/**
 * The last breadcrumb: everything the app needs is up and the routes are
 * rendering. A boot trace WITHOUT this mark is a start-up that never finished —
 * see utils/bootTrace.
 */
function BootComplete() {
    useEffect(() => markBoot('app'), []);
    return null;
}

/**
 * Writes down where the user is, on every navigation — once, above both apps,
 * so no screen can forget to (see useRememberLocation).
 */
function RememberLocation() {
    useRememberLocation();
    return null;
}

/**
 * The Android app saves a download on the device after the click has returned;
 * if that fails, this is where the user hears of it (see lib/nativeApp).
 */
function NativeSaveNotice() {
    const { notify } = useToast();
    const intl = useIntl();
    useEffect(
        () =>
            onSaveInAppFailed((fileName) =>
                notify(
                    intl.formatMessage(
                        { id: 'nativeApp.saveFailed' },
                        { fileName }
                    ),
                    'error'
                )
            ),
        [notify, intl]
    );
    return null;
}

export default function App() {
    // Development only, compiled away in a build: `?fault=crash` throws here,
    // where AppErrorBoundary catches it exactly as it would a real one.
    if (requestedFault() === 'crash') {
        throw new Error('Testabsturz (?fault=crash)');
    }
    return (
        <BrowserRouter>
            <AuthProvider>
                <RxDBProvider>
                    <UserDirectoryProvider>
                        <AbilityProvider>
                            <ConflictProvider>
                                <ToastProvider>
                                    <ConfirmProvider>
                                        <BootComplete />
                                        <RememberLocation />
                                        <NativeSaveNotice />
                                        <Routes>
                                            {/* The root owns no UI — it picks an app
                                        based on role + last use. */}
                                            <Route
                                                path="/"
                                                element={<RootRedirect />}
                                            />
                                            {/* A picture's direct link, in
                                        neither app until the roles say which. */}
                                            <Route
                                                path="/media/:mediaId"
                                                element={<MediaRedirect />}
                                            />
                                            {/* The mobile field tool has its own
                                        touch-first chrome (FieldLayout). */}
                                            <Route
                                                path="/field"
                                                element={<FieldLayout />}
                                            >
                                                <Route
                                                    index
                                                    element={
                                                        <FieldProjectsPage />
                                                    }
                                                />
                                                {/* Zeiterfassung used to be picked
                                            before a project; now it's a
                                            category within one. */}
                                                <Route
                                                    path="time-entries"
                                                    element={
                                                        <Navigate
                                                            to="/field"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path="*"
                                                    element={<NotFoundPage />}
                                                />
                                                <Route path=":projectId">
                                                    <Route
                                                        index
                                                        element={
                                                            <Navigate
                                                                to="findings"
                                                                replace
                                                            />
                                                        }
                                                    />
                                                    {/* The Befund / Planum / Profil
                                                editors reuse the office
                                                fullscreen dialogs, mounted as
                                                child routes over the Befunde
                                                list (its <Outlet />). */}
                                                    <Route
                                                        path="findings"
                                                        element={
                                                            <FieldFindingsPage />
                                                        }
                                                    >
                                                        <Route
                                                            path="new"
                                                            element={
                                                                <FieldFindingDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":findingId/edit"
                                                            element={
                                                                <FieldFindingDialogRoute mode="edit" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":findingId/planums/new"
                                                            element={
                                                                <FieldPlanumDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":findingId/planums/:planumId/edit"
                                                            element={
                                                                <FieldPlanumDialogRoute mode="edit" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":findingId/profiles/new"
                                                            element={
                                                                <FieldProfileDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":findingId/profiles/:profileId/edit"
                                                            element={
                                                                <FieldProfileDialogRoute mode="edit" />
                                                            }
                                                        />
                                                    </Route>
                                                    {/* Gesamtbeschreibungen + Tagebuch
                                                reuse the office fullscreen
                                                dialogs, mounted as child routes
                                                over each list (its <Outlet />). */}
                                                    <Route
                                                        path="overall-descriptions"
                                                        element={
                                                            <FieldOverallDescriptionsPage />
                                                        }
                                                    >
                                                        <Route
                                                            path="new"
                                                            element={
                                                                <FieldOverallDescriptionDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":overallDescriptionId/edit"
                                                            element={
                                                                <FieldOverallDescriptionDialogRoute mode="edit" />
                                                            }
                                                        />
                                                    </Route>
                                                    <Route
                                                        path="area-descriptions"
                                                        element={
                                                            <FieldAreaDescriptionsPage />
                                                        }
                                                    >
                                                        <Route
                                                            path="new"
                                                            element={
                                                                <FieldAreaDescriptionDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":areaDescriptionId/edit"
                                                            element={
                                                                <FieldAreaDescriptionDialogRoute mode="edit" />
                                                            }
                                                        />
                                                    </Route>
                                                    <Route
                                                        path="samples"
                                                        element={
                                                            <FieldSamplesPage />
                                                        }
                                                    >
                                                        <Route
                                                            path="new"
                                                            element={
                                                                <FieldSampleDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":sampleId/edit"
                                                            element={
                                                                <FieldSampleDialogRoute mode="edit" />
                                                            }
                                                        />
                                                    </Route>
                                                    <Route
                                                        path="time-entries"
                                                        element={
                                                            <FieldTimeEntriesPage />
                                                        }
                                                    />
                                                    <Route
                                                        path="equipment-time-entries"
                                                        element={
                                                            <FieldEquipmentTimeEntriesPage />
                                                        }
                                                    />
                                                    <Route
                                                        path="diaries"
                                                        element={
                                                            <FieldDiaryPage />
                                                        }
                                                    >
                                                        <Route
                                                            path="new"
                                                            element={
                                                                <FieldDiaryDialogRoute mode="new" />
                                                            }
                                                        />
                                                        <Route
                                                            path=":diaryId/edit"
                                                            element={
                                                                <FieldDiaryDialogRoute mode="edit" />
                                                            }
                                                        />
                                                    </Route>
                                                </Route>
                                            </Route>
                                            {/* Field-only crew has no business here,
                                        however they got to the URL — see
                                        RequireOffice. */}
                                            <Route
                                                path="/office/*"
                                                element={
                                                    <RequireOffice>
                                                        <Suspense
                                                            fallback={
                                                                <FullPageProgress />
                                                            }
                                                        >
                                                            <OfficeApp />
                                                        </Suspense>
                                                    </RequireOffice>
                                                }
                                            />
                                            {/* Pre-split bookmarks. */}
                                            <Route
                                                path="/projects/*"
                                                element={
                                                    <LegacyOfficeRedirect />
                                                }
                                            />
                                            <Route
                                                path="/time-entries/*"
                                                element={
                                                    <LegacyOfficeRedirect />
                                                }
                                            />
                                            <Route
                                                path="/admin/*"
                                                element={
                                                    <LegacyOfficeRedirect />
                                                }
                                            />
                                            <Route
                                                path="*"
                                                element={<NotFoundPage />}
                                            />
                                        </Routes>
                                    </ConfirmProvider>
                                </ToastProvider>
                            </ConflictProvider>
                        </AbilityProvider>
                    </UserDirectoryProvider>
                </RxDBProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}
